import React, { useState } from 'react';

import ConfirmModal from 'components/core/ConfirmModal';
import Text from 'components/core/Text';
import { CloseIcon } from 'components/Icons';

import { TMediaItem, TMediaType, useDeleteMedia, useMediaList } from 'api/media';

import {
  StyledCard,
  StyledCardWrap,
  StyledDeleteButton,
  StyledGrid,
  StyledLoadMore,
  StyledPlayBadge,
  StyledSpinner,
  StyledState,
  StyledThumb,
  StyledThumbVideo,
  StyledThumbWrap,
  StyledTitle,
} from './MediaGrid.styles';

type TMediaGridProps = {
  onSelect: (item: TMediaItem) => void;
  mediaTypes?: TMediaType[];
};

const MediaGrid = ({ onSelect, mediaTypes }: TMediaGridProps) => {
  const { items, isLoading, isError, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useMediaList();
  const { removeMedia, isPending: isDeleting } = useDeleteMedia();
  const [deleteTarget, setDeleteTarget] = useState<TMediaItem | null>(null);
  const visible = mediaTypes ? items.filter((item) => mediaTypes.includes(item.mediaType)) : items;

  const confirmDelete = async () => {
    if (!deleteTarget) {
      return;
    }
    try {
      await removeMedia(deleteTarget.id);
    } finally {
      setDeleteTarget(null);
    }
  };

  if (isLoading) {
    return (
      <StyledState>
        <StyledSpinner />
        <Text variant="caption" color="textMuted">
          Loading media…
        </Text>
      </StyledState>
    );
  }

  if (isError) {
    return (
      <StyledState>
        <Text variant="body" color="textPrimary">
          Couldn’t load media.
        </Text>
        <Text variant="caption" color="textMuted">
          Check that the media service is running.
        </Text>
      </StyledState>
    );
  }

  if (visible.length === 0) {
    return (
      <StyledState>
        <Text variant="body" color="textPrimary">
          No media yet
        </Text>
        <Text variant="caption" color="textMuted">
          Uploaded media will appear here.
        </Text>
      </StyledState>
    );
  }

  return (
    <>
      <StyledGrid>
        {visible.map((item) => (
          <StyledCardWrap key={item.id}>
            <StyledCard type="button" title={item.title} onClick={() => onSelect(item)}>
              <StyledThumbWrap>
                {item.mediaType === 'VIDEO' ? (
                  <>
                    <StyledThumbVideo src={item.mediaUrl} muted playsInline preload="metadata">
                      <track kind="captions" />
                    </StyledThumbVideo>
                    <StyledPlayBadge>▶</StyledPlayBadge>
                  </>
                ) : (
                  <StyledThumb src={item.mediaUrl} alt={item.title} loading="lazy" />
                )}
              </StyledThumbWrap>
              <StyledTitle>{item.title}</StyledTitle>
            </StyledCard>
            <StyledDeleteButton
              type="button"
              data-delete
              aria-label={`Delete ${item.title}`}
              onClick={() => setDeleteTarget(item)}>
              <CloseIcon size={12} />
            </StyledDeleteButton>
          </StyledCardWrap>
        ))}
      </StyledGrid>

      {hasNextPage ? (
        <StyledLoadMore type="button" disabled={isFetchingNextPage} onClick={() => fetchNextPage()}>
          {isFetchingNextPage ? 'Loading…' : 'Load more'}
        </StyledLoadMore>
      ) : null}

      <ConfirmModal
        isOpen={deleteTarget !== null}
        title="Delete media"
        message={`Are you sure you want to delete “${deleteTarget?.title ?? ''}”? This can’t be undone.`}
        confirmLabel={isDeleting ? 'Deleting…' : 'Delete'}
        confirmVariant="danger"
        confirmDisabled={isDeleting}
        onClose={() => (isDeleting ? undefined : setDeleteTarget(null))}
        onConfirm={confirmDelete}
      />
    </>
  );
};

export default MediaGrid;
